import { cn } from "@/lib/utils"

function Skeleton({
  className,
  variant = 'default',
  width,
  height,
  animation = 'shimmer',
  count = 1,
  ...props
}) {
  const baseClasses = animation === 'shimmer'
    ? 'bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] animate-shimmer'
    : 'animate-pulse bg-slate-200';

  const variantClasses = {
    default: 'rounded-md',
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
    card: 'rounded-xl h-40',
  };

  const skeletonStyle = {
    width: width || '100%',
    height: height || (variant === 'text' ? '1rem' : variant === 'card' ? '10rem' : undefined),
  };

  const skeletons = Array.from({ length: count }, (_, index) => (
    <div
      key={index}
      className={cn(baseClasses, variantClasses[variant] || variantClasses.default, className)}
      style={skeletonStyle}
      role="status"
      aria-label="Loading..."
      {...props}
    >
      <span className="sr-only">Loading...</span>
    </div>
  ));

  return count > 1 ? <div className="space-y-3">{skeletons}</div> : skeletons[0];
}

/**
 * Pre-built skeleton patterns for common UI components
 */
const StatCardSkeleton = ({ count = 4 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 animate-scale-in" style={{ animationDelay: `${i * 0.05}s` }}>
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-3">
            <Skeleton variant="text" width="60%" height="0.875rem" />
            <Skeleton variant="text" width="80%" height="2.5rem" />
            <Skeleton variant="text" width="50%" height="0.75rem" />
          </div>
          <Skeleton variant="circular" width="3.5rem" height="3.5rem" />
        </div>
      </div>
    ))}
  </div>
);

const CardSkeleton = ({ count = 1 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 space-y-3">
            <Skeleton variant="text" width="60%" height="1.5rem" />
            <Skeleton variant="text" width="40%" height="1rem" />
          </div>
          <Skeleton variant="circular" width="3rem" height="3rem" />
        </div>
        <Skeleton variant="rectangular" width="100%" height="8rem" />
      </div>
    ))}
  </div>
);

const TableRowSkeleton = ({ rows = 5, columns = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }, (_, rowIndex) => (
      <div key={rowIndex} className="flex gap-4 p-4 bg-slate-50 rounded-lg">
        {Array.from({ length: columns }, (_, colIndex) => (
          <Skeleton
            key={colIndex}
            variant="text"
            width={colIndex === 0 ? '30%' : '20%'}
          />
        ))}
      </div>
    ))}
  </div>
);

export { Skeleton, StatCardSkeleton, CardSkeleton, TableRowSkeleton }
